import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageTitle, Card, Field, TextInput, TextArea, Btn, ListEditor } from "@/components/admin/ui";
import { ImageField, UploadButton } from "@/components/admin/Media";
import { Switch } from "@/components/ui/switch";
import { slugify } from "@/lib/format";

const BLANK = {
  name: "", slug: "", tagline: "", category: "", badge: "", description: "", hero_image: "",
  colors: [], variants: [], quick_specs: [], specs: [], highlights: "", features: "", gallery: "",
  seats: 5, order: 0, published: true,
};

const toForm = (v) => ({ ...v, highlights: (v.highlights || []).join("\n"), features: (v.features || []).join("\n"), gallery: (v.gallery || []).join("\n") });
const lines = (s) => String(s || "").split("\n").map((x) => x.trim()).filter(Boolean);
const num = (x) => (x === "" || x == null ? null : Number(x));

export default function VehicleEditor() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);

  const { data, isLoading } = useQuery({ queryKey: ["admin-vehicle", id], queryFn: async () => (await api.get(`/admin/vehicles/${id}`)).data, enabled: !isNew, staleTime: 0 });
  useEffect(() => { if (data) { setForm(toForm(data)); setSlugTouched(true); } }, [data]);

  const set = (k) => (e) => {
    const val = e?.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e;
    setForm((f) => ({ ...f, [k]: val, ...(k === "name" && !slugTouched ? { slug: slugify(val) } : {}) }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.slug.trim() || !form.hero_image.trim()) return toast.error("Name, slug and hero image are required.");
    const { id: _id, created_at, updated_at, ...rest } = form;
    const body = {
      ...rest,
      seats: Number(form.seats) || 5,
      order: Number(form.order) || 0,
      highlights: lines(form.highlights), features: lines(form.features), gallery: lines(form.gallery),
      variants: form.variants.filter((v) => v.name).map((v) => ({ ...v, price: num(v.price), booking_amount: num(v.booking_amount), price_label: v.price_label || "Ex-Factory", note: v.note || "" })),
      colors: form.colors.filter((c) => c.name && c.image).map((c) => ({ ...c, hex: c.hex || "#111111" })),
      quick_specs: form.quick_specs.filter((s) => s.label && s.value),
      specs: form.specs.filter((s) => s.label && s.value),
    };
    setSaving(true);
    try {
      if (isNew) await api.post("/admin/vehicles", body); else await api.put(`/admin/vehicles/${id}`, body);
      toast.success(isNew ? "Vehicle created" : "Vehicle saved");
      qc.invalidateQueries({ queryKey: ["admin-vehicles"] }); qc.invalidateQueries({ queryKey: ["vehicles"] }); qc.invalidateQueries({ queryKey: ["vehicle"] }); qc.invalidateQueries({ queryKey: ["admin-vehicle", id] });
      navigate("/admin/vehicles");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!isNew && isLoading) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-crimson" /></div>;

  return (
    <form onSubmit={save} data-testid="vehicle-editor-form">
      <Link to="/admin/vehicles" className="mb-4 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-slate-400 hover:text-white" data-testid="vehicle-editor-back"><ArrowLeft className="h-3 w-3" /> Vehicles</Link>
      <PageTitle eyebrow={isNew ? "New model" : "Edit model"} title={form.name || "Untitled vehicle"}>
        <label className="flex items-center gap-3 font-display text-sm"><Switch checked={form.published} onCheckedChange={set("published")} data-testid="vehicle-published-switch" /> Published</label>
        <Btn type="submit" disabled={saving} data-testid="vehicle-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</Btn>
      </PageTitle>

      <div className="grid gap-6 xl:grid-cols-3">
        <div className="space-y-6 xl:col-span-2">
          <Card title="Basics">
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Model name"><TextInput value={form.name} onChange={set("name")} placeholder="JETOUR T2 i-DM" data-testid="vehicle-name-input" required /></Field>
              <Field label="URL slug" hint="Used in the web address: /models/your-slug"><TextInput value={form.slug} onChange={(e) => { setSlugTouched(true); set("slug")(e); }} placeholder="t2-idm" data-testid="vehicle-slug-input" required /></Field>
              <Field label="Tagline"><TextInput value={form.tagline} onChange={set("tagline")} placeholder="Think Adventure" data-testid="vehicle-tagline-input" /></Field>
              <Field label="Category"><TextInput value={form.category} onChange={set("category")} placeholder="Plug-in Hybrid SUV" data-testid="vehicle-category-input" /></Field>
              <Field label="Badge" hint="Short label e.g. New, PHEV, 7 Seats"><TextInput value={form.badge} onChange={set("badge")} data-testid="vehicle-badge-input" /></Field>
              <div className="grid grid-cols-2 gap-5">
                <Field label="Seats"><TextInput type="number" value={form.seats} onChange={set("seats")} data-testid="vehicle-seats-input" /></Field>
                <Field label="Display order"><TextInput type="number" value={form.order} onChange={set("order")} data-testid="vehicle-order-input" /></Field>
              </div>
              <Field label="Description" className="sm:col-span-2"><TextArea rows={4} value={form.description} onChange={set("description")} data-testid="vehicle-description-input" /></Field>
            </div>
          </Card>

          <Card title="Variants & pricing">
            <ListEditor testid="variants-editor" label="Variants" rows={form.variants} onChange={set("variants")} blank={{ name: "", price: "", price_label: "Ex-Factory", booking_amount: "", note: "" }}
              fields={[{ key: "name", placeholder: "Variant name" }, { key: "price", placeholder: "Price (PKR)", type: "number" }, { key: "price_label", placeholder: "Label (Ex-Factory)" }, { key: "booking_amount", placeholder: "Booking (PKR)", type: "number" }, { key: "note", placeholder: "Note" }]} />
            <p className="mt-3 text-xs text-slate-500">Leave price empty for "Price to be announced". Use label "Add-on" for optional packages so they don't affect the "From" price.</p>
          </Card>

          <Card title="Specifications">
            <div className="space-y-8">
              <ListEditor testid="quick-specs-editor" label="Quick specs (max 4, shown on cards)" rows={form.quick_specs} onChange={set("quick_specs")} blank={{ label: "", value: "" }} max={4} fields={[{ key: "label", placeholder: "Power" }, { key: "value", placeholder: "360 hp" }]} />
              <ListEditor testid="specs-editor" label="Full specifications" rows={form.specs} onChange={set("specs")} blank={{ label: "", value: "" }} fields={[{ key: "label", placeholder: "Engine" }, { key: "value", placeholder: "1.5L TGDI" }]} />
            </div>
          </Card>

          <Card title="Highlights & equipment">
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Highlights" hint="One per line"><TextArea rows={6} value={form.highlights} onChange={set("highlights")} data-testid="vehicle-highlights-input" /></Field>
              <Field label="Standard equipment" hint="One per line"><TextArea rows={6} value={form.features} onChange={set("features")} data-testid="vehicle-features-input" /></Field>
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          <Card title="Hero image">
            <Field label="Image" hint="Transparent PNG of the car works best. Upload, pick from the library, or paste a URL.">
              <ImageField value={form.hero_image} onChange={(v) => setForm((f) => ({ ...f, hero_image: v }))} placeholder="/vehicles/t2/rock-grey.png" testid="vehicle-hero-image" />
            </Field>
            <div className="spotlight mt-4 flex h-40 items-center justify-center rounded-md border border-white/10 bg-obsidian">
              {form.hero_image ? <img src={form.hero_image} alt="" className="max-h-32 object-contain" data-testid="vehicle-hero-preview" /> : <span className="text-xs text-slate-600">Preview</span>}
            </div>
          </Card>
          <Card title="Colours">
            <ListEditor testid="colors-editor" label="Available colours" rows={form.colors} onChange={set("colors")} blank={{ name: "", hex: "#111111", image: "" }} fields={[{ key: "name", placeholder: "Carbon Black" }, { key: "hex", placeholder: "#0e0f12" }, { key: "image", placeholder: "Image", type: "image" }]} />
          </Card>
          <Card title="Extra gallery" action={<UploadButton multiple testid="vehicle-gallery-upload" onUploaded={(item) => setForm((f) => ({ ...f, gallery: [f.gallery, item.url].filter(Boolean).join("\n") }))} className="font-mono text-[11px] uppercase tracking-[0.16em] text-crimson">Upload</UploadButton>}>
            <Field label="Image URLs" hint="One per line — uploads are added automatically"><TextArea rows={5} value={form.gallery} onChange={set("gallery")} data-testid="vehicle-gallery-input" /></Field>
          </Card>
        </div>
      </div>
    </form>
  );
}
